import { Button } from "@components";
import { useEffect, useMemo, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Currency } from "shared/utils/Currency";
import ComboFilters from "../ComboFilters";
import { Numbers } from "shared/utils/Numbers";
import { formatByType } from "../Reports";

export default function CampaignsInPeriod({ data }) {
  const [selectedOption, setSelectedOption] = useState({
    name: "Faturamento total",
    id: "total",
  });

  const _data = {
    total: {
      series: ["Faturamento influenciado", "Faturamento com cupons"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.influenced?.value || 0),
        data?.map((d) => d.total_with_discounts?.value || 0),
      ],
    },
    uses: {
      series: ["Qtd. de Usos"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data?.map((d) => d.uses.value || 0)],
    },
    clicks: {
      series: ["Qtd. de Cliques"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data?.map((d) => d.clicks.value || 0)],
    },
    opens: {
      series: ["Qtd. de Aberturas"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data?.map((d) => d.opens.value || 0)],
    },
    sends: {
      series: ["Qtd. de Disparos"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data?.map((d) => d.sends.value || 0)],
    },
    cost: {
      series: ["Custo com disparos", "Descontos de cupom"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.send_cost?.value || 0),
        data?.map((d) => d.discounts?.value || 0),
      ],
    },
    send_cost: {
      series: ["Custo com disparos"],
      value_type: "currency",
      colors: ["#5AE8F4"],
      data: [data?.map((d) => d.send_cost.value || 0)],
    },
    discounts: {
      series: ["Descontos de cupom"],
      value_type: "currency",
      colors: ["#5AE8F4"],
      data: [data?.map((d) => d.discounts.value || 0)],
    },
    total_with_discounts: {
      series: ["Faturamento com cupons"],
      value_type: "currency",
      colors: ["#5AE8F4"],
      data: [data?.map((d) => d.total_with_discounts.value || 0)],
    },
    influenced: {
      series: ["Faturamento influenciado"],
      value_type: "currency",
      colors: ["#FF9F68"],
      data: [data?.map((d) => d.influenced.value || 0)],
    },
  };

  const totalData = [];

  const getDataByIndex = (index) => {
    return data[index];
  };

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("campaigns-in-period-chart");

      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });

      const selectedOptionId = selectedOption.id;

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          backgroundColor: "#2B2B2B",
          borderRadius: 8,
          padding: [8, 8],
          borderWidth: 0,
          textStyle: {
            color: "#FFF",
            fontSize: 10,
          },
          formatter: (params) => {
            const index = params[0].dataIndex;
            const item = getDataByIndex(index);

            const formatGrowth = (growth) => {
              if (growth === 0) return "";

              const isPositive = growth > 0;
              const color = isPositive ? "#51D9A3" : "#e48186";
              const sign = isPositive ? "+" : "";
              const arrow = isPositive ? "↑" : "↓";

              return `
                <span style="color:${color};font-weight:800;margin-left:4px;">
                  ${sign}${(growth || 0).toFixed(2)}% ${arrow}
                </span>
              `;
            };

            return `
              <div>
                <div style="border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <div style="color:#fff;opacity:70%;line-height:16px;font-weight:600;margin-bottom:2px;">
                    ${item.date}
                  </div>
                </div>

                ${
                  selectedOption.id === "total"
                    ? `
                    <div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#8a8af5;font-weight:700">
                        Faturamento total:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.total.value)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Faturamento influenciado:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.influenced.value)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Faturamento com cupons:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(
                          +item.total_with_discounts?.value
                        )} 
                      </span>  
                    </div>
                `
                    : ""
                }

                ${
                  selectedOption.id === "cost"
                    ? `
                    <div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#8a8af5;font-weight:700">
                        Custo total:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.cost.value)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Custo com disparos:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.send_cost.value)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Desconto de cupom:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.discounts.value)} 
                      </span>  
                    </div>
                `
                    : ""
                }

                ${
                  selectedOption.id === "total_with_discounts"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#8a8af5;font-weight:700">
                    Faturamento com cupons:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(
                      +item.total_with_discounts?.value
                    )} 
                  </span>  
                </div>`
                    : ""
                }

                ${
                  selectedOption.id === "influenced"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#8a8af5;font-weight:700">
                    Faturamento influenciado:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Currency.formatCurrency(+item.influenced.value)} 
                  </span>  
                </div>`
                    : ""
                }

                ${
                  selectedOption.id === "sends"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#8a8af5;font-weight:700">
                    Qtd. de disparos:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Numbers.formatInt(item.sends.value)} 
                  </span>  
                </div>`
                    : ""
                }

                ${
                  selectedOption.id === "opens"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#8a8af5;font-weight:700">
                    Qtd. de aberturas:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Numbers.formatInt(item.opens.value)} 
                  </span>  
                </div>`
                    : ""
                }

                ${
                  selectedOption.id === "clicks"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#8a8af5;font-weight:700">
                    Qtd. de cliques:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Numbers.formatInt(item.clicks.value)} 
                  </span>  
                </div>`
                    : ""
                }

                ${
                  selectedOption.id === "uses"
                    ? `<div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                  <span style="color:#8a8af5;font-weight:700">
                    Qtd. de usos:
                  </span>  
                  <span style="color:#fff;margin-left:4px;">
                    ${Numbers.formatInt(item.uses.value)} 
                  </span>  
                </div>`
                    : ""
                }

                ${
                  selectedOption.id !== "total"
                    ? `<div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Faturamento total:</span>
                  <span style="color:#fff">${Currency.formatCurrency(
                    +item.total.value
                  )}</span>
                </div>`
                    : ""
                }

                                ${
                                  selectedOption.id !== "influenced" &&
                                  selectedOption.id !== "total"
                                    ? `<div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Faturamento influenciado:</span>
                  <span style="color:#fff">${Currency.formatCurrency(
                    +item.influenced.value
                  )}</span>
                </div>`
                                    : ""
                                }

                ${
                  selectedOption.id !== "total_with_discounts" &&
                  selectedOption.id !== "total"
                    ? `<div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Faturamento com cupons:</span>
                  <span style="color:#fff">${Currency.formatCurrency(
                    +item.total_with_discounts?.value
                  )}</span>
                </div>`
                    : ""
                }

                                ${
                                  selectedOption.id !== "cost"
                                    ? `<div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Custo total:</span>
                  <span style="color:#fff">${Currency.formatCurrency(
                    +item.cost.value
                  )}</span>
                </div>`
                                    : ""
                                }

                ${
                  selectedOption.id !== "send_cost" &&
                  selectedOption.id !== "cost"
                    ? `<div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Custo com disparos:</span>
                  <span style="color:#fff">${Currency.formatCurrency(
                    +item.send_cost.value
                  )}</span>
                </div>`
                    : ""
                }

                ${
                  selectedOption.id !== "discounts" &&
                  selectedOption.id !== "cost"
                    ? `<div style="line-height:20px">
                  <span style="color:#fff;opacity:50%">Desconto de cupom:</span>
                  <span style="color:#fff">${Currency.formatCurrency(
                    +item.discounts.value
                  )}</span>
                </div>`
                    : ""
                }

                ${
                  selectedOption.id !== "sends"
                    ? `<div style="line-height:20px">
                        <span style="color:#fff;opacity:50%">Qtd. de disparos:</span>
                        <span style="color:#fff">${Numbers.formatInt(
                          item.sends.value
                        )}</span>
                      </div>`
                    : ""
                }

                ${
                  selectedOption.id !== "opens"
                    ? `<div style="line-height:20px">
                        <span style="color:#fff;opacity:50%">Qtd. de aberturas:</span>
                        <span style="color:#fff">${Numbers.formatInt(
                          item.opens.value
                        )}</span>
                      </div>`
                    : ""
                }

                ${
                  selectedOption.id !== "clicks"
                    ? `<div style="line-height:20px">
                        <span style="color:#fff;opacity:50%">Qtd. de cliques:</span>
                        <span style="color:#fff">${Numbers.formatInt(
                          item.clicks.value
                        )}</span>
                      </div>`
                    : ""
                }

                ${
                  selectedOption.id !== "uses"
                    ? `<div style="line-height:20px">
                        <span style="color:#fff;opacity:50%">Qtd. de usos:</span>
                        <span style="color:#fff">${Numbers.formatInt(
                          item.uses.value
                        )}</span>
                      </div>`
                    : ""
                }

              </div>
            `;
          },
        },
        grid: {
          left: 0,
          right: 10,
          top: 30,
          bottom: 0,
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: data?.map((d) => d.date),
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLine: {
              lineStyle: {
                color: "rgba(217, 217, 217, 0.5)",
              },
            },
            splitLine: {
              lineStyle: {
                color: "rgba(51, 51, 51, 0.55)",
                width: 1,
                type: "solid",
              },
            },
            axisLabel: {
              formatter: (value) =>
                [
                  "total",
                  "cost",
                  "influenced",
                  "total_with_discounts",
                ].includes(selectedOption.id)
                  ? Currency.formatCurrency(value)
                  : Numbers.formatInt(value),
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        series: _data?.[selectedOptionId]?.series.map((name, sid, series) => {
          return {
            name,
            type: "bar",
            stack: "total",
            barWidth: "80%",
            data: _data?.[selectedOptionId]?.data?.[sid]?.map((d, did) =>
              totalData[did] <= 0 ? 0 : d
            ),
            label: {
              show: sid == series.length - 1,
              position: "top",
              lineHeight: 20,
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: 10,
              fontWeight: "semibold",
              formatter: (params) => {
                const index = params.dataIndex;
                let total = 0;

                if (selectedOptionId === "total") {
                  total =
                    _data?.["influenced"]?.data?.[0]?.[index] +
                    _data?.["total_with_discounts"]?.data?.[0]?.[index];

                  if (total === 0) return "";
                } else if (selectedOptionId === "cost") {
                  total =
                    _data?.["send_cost"]?.data?.[0]?.[index] +
                    _data?.["discounts"]?.data?.[0]?.[index];

                  if (total === 0) return "";
                } else {
                  total =
                    _data[selectedOptionId].data[0][index] ||
                    _data[selectedOptionId].data[0][index];

                  if (total === 0) return "";
                }

                return formatByType(
                  total,
                  _data?.[selectedOptionId]?.value_type
                );
              },
            },
            itemStyle: {
              color: _data?.[selectedOptionId]?.colors[sid],
            },
          };
        }),
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption]);

  const rawData = _data?.[selectedOption.id]?.data[0] || [];
  const rawData2 = _data?.[selectedOption.id]?.data[1] || [];

  const normalizedData = (data) => {
    data.map((arr) => {
      if (Array.isArray(arr))
        return arr.map((d) => (typeof d === "object" ? d.value : d));

      return arr;
    });

    return data;
  };

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Campanhas no período</p>
        </div>
        <ComboFilters
          options={[
            { name: "Faturamento total", id: "total" },
            { name: "Custo total", id: "cost" },
            { name: "Qtd. de disparos", id: "sends" },
            { name: "Qtd. de aberturas", id: "opens" },
            { name: "Qtd. de cliques", id: "clicks" },
            { name: "Qtd. de usos", id: "uses" },
          ]}
          onSelect={(option) => setSelectedOption(option)}
          selectedOption={
            selectedOption || { name: "Faturamento total", id: "total" }
          }
        />
      </div>

      <div className={styles.chart}>
        {selectedOption?.id === "total" ? (
          normalizedData(rawData)?.every((s) => s === 0 || s === null) &&
          normalizedData(rawData2)?.every((s) => s === 0 || s === null)
        ) : normalizedData(rawData)?.every((s) => s === 0 || s === null) ? (
          <div className={styles.emptyChart} style={{ height: 350 }}>
            <span className="text-grey">Não há dados neste período</span>
          </div>
        ) : null}

        <div
          id="campaigns-in-period-chart"
          style={{ width: "100%", height: 350 }}
        />
      </div>
    </div>
  );
}
